import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getImageUrl } from '../utils/imageUtils';
import useCustomFetchApi from '../utils/customFetchApi';
import LazyFlag from './LazyFlag';
import './PortraitDetailsModal.scss'; // Already using .scss

function PortraitDetailsModal({ portrait, onClose, onOutsideClick }) { // Add outside click handler
  const [isExpanded, setIsExpanded] = useState(false); // State for "Read More"
  const [isImageZoomed, setIsImageZoomed] = useState(false); // State for image zoom
  const [isFullscreen, setIsFullscreen] = useState(false); // State for fullscreen image
  const [imageLoaded, setImageLoaded] = useState(false); // State for image loading
  const [imageError, setImageError] = useState(false); // State for image error
  const [freshPortrait, setFreshPortrait] = useState(portrait); // State for fresh portrait data
  const [isLoadingPortrait, setIsLoadingPortrait] = useState(false); // Loading state for API call
  
  const api = useCustomFetchApi();

  // Function to fetch fresh portrait data
  const fetchFreshPortrait = async () => {
    if (!portrait?.uuid) return;
    
    setIsLoadingPortrait(true);
    try {
      const response = await api.get(`/user/${portrait.uuid}`);
      if (response) {
        // Format the date from API response
        let joined = '';
        if (response.created_at) {
          const date = new Date(response.created_at);
          if (!isNaN(date.getTime())) {
            joined = date.toLocaleDateString();
          }
        }
        
        // Set fresh portrait with formatted date
        setFreshPortrait({
          ...response,
          joined
        });
      }
    } catch (error) {
      console.error('Error fetching fresh portrait data:', error);
    } finally {
      setIsLoadingPortrait(false);
    }
  };

  // Fetch fresh data when modal opens
  useEffect(() => {
    if (portrait?.uuid) {
      fetchFreshPortrait();
    }
  }, [portrait?.uuid]);

  // Lock body scroll when modal is open
  useEffect(() => {
    // Save current scroll position
    const scrollY = window.scrollY;
    
    // Lock scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Cleanup: restore scroll
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const toggleExpand = () => setIsExpanded(!isExpanded); // Toggle "Read More" and "Read Less"
  const toggleImageZoom = () => setIsImageZoomed(!isImageZoomed); // Toggle image zoom
  const toggleFullscreen = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setIsFullscreen(!isFullscreen);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isFullscreen]);

  // Reset zoom and fullscreen when portrait changes
  useEffect(() => {
    setIsImageZoomed(false);
    setIsFullscreen(false);
    setImageLoaded(false);
    setImageError(false);
    setFreshPortrait(portrait); // Reset to initial portrait when prop changes
  }, [portrait]);

  // Use fresh portrait data or fallback to original
  const currentPortrait = freshPortrait || portrait;

  const modalContent = (
    <>
      {/* Main modal - hide when fullscreen is active */}
      {!isFullscreen && (
        <div className="overlay" id="portrait-popup" onClick={onOutsideClick}>
          <div className={`modal ${isImageZoomed ? 'image-zoomed' : ''}`}>
            <div className="modal-content">
              <button className="close-button" onClick={onClose}>×</button>
            
            {/* Enhanced image container with zoom functionality */}
            <div className="image-container">
              {/* API Loading state */}
              {isLoadingPortrait && (
                <div className="image-loading">
                  <div className="loading-spinner"></div>
                  <p>Fetching latest data...</p>
                </div>
              )}
              
              {/* Image Loading state */}
              {!imageLoaded && !imageError && !isLoadingPortrait && (
                <div className="image-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading portrait...</p>
                </div>
              )}
              
              {/* Error state */}
              {/* {imageError && (
                <div className="image-error">
                  <div className="error-icon">⚠</div>
                  <p>Failed to load image</p>
                  <button 
                    className="retry-button"
                    onClick={() => {
                      setImageError(false);
                      setImageLoaded(false);
                    }}
                  >
                    Retry
                  </button>
                </div>
              )} */}
              
              {/* Only render image if we have a valid image URL */}
              {currentPortrait.images && currentPortrait.images.length > 0 && currentPortrait.images[0].image_path && (
                <img 
                  src={getImageUrl(currentPortrait.images[0].image_path)}
                  alt={currentPortrait.name}
                  className={`${isImageZoomed ? 'zoomed' : ''} ${imageLoaded ? 'loaded' : ''}`}
                  onClick={toggleFullscreen}
                  title="Click to view fullscreen"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{ display: imageError ? 'none' : 'block' }}
                />
              )}

              {/* Show placeholder if no image available */}
              {(!currentPortrait.images || !currentPortrait.images.length || !currentPortrait.images[0].image_path) && (
                <div className="image-placeholder">
                  <div className="placeholder-icon">👤</div>
                  <p>No image available</p>
                </div>
              )}
              
              {/* Image controls overlay - only show when image is loaded */}
              {imageLoaded && (
                <>
                  <div className="image-controls">
                    <button 
                      className="zoom-button"
                      onClick={toggleImageZoom}
                      title={isImageZoomed ? "Zoom Out" : "Zoom In"}
                    >
                      {isImageZoomed ? '−' : '+'}
                    </button>
                    <button 
                      className="fullscreen-button"
                      onClick={toggleFullscreen}
                      title="View Fullscreen"
                    >
                      ⛶
                    </button>
                  </div>
                </>
              )}
            </div>
            
            <div className="content-area">
              <div className="nickname-row">
                <h2>{currentPortrait.nickname}</h2>
                {currentPortrait.country && (
                  <LazyFlag
                    src={`/flags/${currentPortrait.country.toLowerCase()}.webp`} 
                    alt={`${currentPortrait.country} flag`} 
                    className="country-flag"
                  />
                )}
              </div>
              <p className='join-date'>{currentPortrait.joined}</p>
              <p>
                {currentPortrait.message && currentPortrait.message.length > 100 && !isExpanded
                  ? `${currentPortrait.message.substring(0, 100)}...`
                  : currentPortrait.message || ''}
                
                {currentPortrait.message && currentPortrait.message.length > 100 && (
                  <span className="read-more-text" onClick={toggleExpand}>
                    {isExpanded ? ' Read Less' : ' Read More'}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Fullscreen Image Modal */}
      {isFullscreen && currentPortrait.images && currentPortrait.images.length > 0 && currentPortrait.images[0].image_path && (
        <div className="fullscreen-overlay" onClick={toggleFullscreen}>
          <div className="fullscreen-container">
            <img 
              src={getImageUrl(currentPortrait.images[0].image_path)}
              alt={currentPortrait.name}
              className="fullscreen-image"
              onClick={(e) => e.stopPropagation()}
            />
            <button className="fullscreen-close" onClick={toggleFullscreen}>×</button>
            <div className="fullscreen-info">
              <p>{currentPortrait.nickname}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Render modal using portal to document.body
  return ReactDOM.createPortal(modalContent, document.body);
}

export default PortraitDetailsModal;
